import type { User } from '@gm-commercial/profile-model';
import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from '@reduxjs/toolkit';
import merge from 'deepmerge';

import {
  API_VERSION_DEFAULTS,
  DEFAULT_API_CONFIG,
} from '~/common/apis/api.constants';
import type { RouteParams } from '~/common/configs/route.config';
import {
  type ApiResponse,
  BDRequestStatus,
  BDRequestType,
  type OperationStatus,
} from '~/common/models/apis/apiResponse.model';
import type {
  ApiSafetyComplianceSummary,
  Asset,
  SafetyComplianceSummary,
} from '~/common/models/asset.model';
import {
  type AggregateCounts,
  type ContinuationCache,
  type FormPayload,
  type ListViewSession,
  SeverityLevel,
} from '~/common/models/common.model';
import { BDAppErrorType, BDError } from '~/common/models/error.model';
import type { Fleet, FleetFormFields } from '~/common/models/fleet.model';
import {
  addAcceptLanguageHeader,
  addHeader,
  addOrganizationIdHeader,
  hasApiResult,
} from '~/common/utils/apis/api.utils';
import {
  makeDeletePayloadCreator,
  makeGetPayloadCreator,
  makePostPayloadCreator,
  makePutPayloadCreator,
  makeThunk,
} from '~/common/utils/store/thunk.helper';

import type { RootState } from '../../app/rootReducer';
import type { FleetConfigurationViewType } from './details/widget/fleetConfiguration/FleetConfigurationTabWidget';
import type { FleetField, ValidFleetForm } from './form/FleetForm';
import {
  type ApiFleetFields,
  mapApiResponseToFleet,
  mapFleetFormToApiRequest,
} from './mappers/fleet.mappers';

export enum FleetRequestType {
  GET_SAFETY_COMPLIANCE_SUMMARY = 'Get Safety Compliance Summary',
  GET_PAGINATED_FLEET_LIST = 'Get Paginated Fleet List',
}

const fleetsAdapter = createEntityAdapter<Fleet>({
  // redundant, but makes obvious the key used for fetching records
  selectId: (fleet: Fleet) => fleet.id,
});

const fleetSafetyComplianceAdapter = createEntityAdapter<{
  fleetId: string;
  complianceSummary: SafetyComplianceSummary;
}>({
  selectId: (safetyCompliance) => safetyCompliance.fleetId,
});

export enum FleetViewType {
  DETAIL = 'Fleet Detail',
  LIST = 'Fleet List',
  SUBLIST = 'Fleet Sublist',
}
export type FleetConfiguration = Partial<ListViewSession> & {
  currentEditConfigTab?: FleetConfigurationViewType;
  selectedField?: Partial<FleetFormFields>;
  selectedTab?: { [key in FleetConfigurationViewType]?: FleetField };
  isSuccess?: boolean;
};

export interface FleetSettingsSession extends FleetConfiguration {
  isAutoLockOpen?: boolean;
  isDriverSafetySettingsExpanded?: boolean;
  isDetailPropsExpanded?: boolean;
}

type FleetsListCache = ContinuationCache<Fleet[]>;

interface FleetListSession extends ListViewSession {
  fleetCache?: FleetsListCache;
  totalItems: number;
  continuationCache?: FleetsListCache;
}

export interface FleetSessionConfigType {
  [FleetViewType.DETAIL]?: {
    [id: string]: Partial<FleetSettingsSession>;
  };
  [FleetViewType.LIST]?: {
    [id: string]: Partial<FleetListSession>;
  };
  [FleetViewType.SUBLIST]?: {
    [id: string]: Partial<FleetListSession>;
  };
}

interface FleetsState {
  operations: {
    [key in BDRequestType | FleetRequestType]?: OperationStatus;
  };
  sessionConfigs: {
    [k in FleetViewType]?: FleetSessionConfigType[k];
  };
  safetyCompliance: EntityState<{
    fleetId: string;
    complianceSummary: SafetyComplianceSummary;
  }>;
  lastCreatedFleetId: string;
}

interface PagedResult<T> {
  items: T[];
  total_items: number;
  continuation_token?: string;
}

interface PaginationContinuationInfo {
  continuationToken: string | null;
  page: number;
  size: number;
}

const initialState = fleetsAdapter.getInitialState<FleetsState>({
  operations: {},
  sessionConfigs: {},
  safetyCompliance: fleetSafetyComplianceAdapter.getInitialState(),
  lastCreatedFleetId: '',
});

export type FleetRouteParams = Required<Pick<RouteParams, 'organizationsId'>> &
  Partial<Pick<RouteParams, 'fleetsId'>>;

export type FleetComplianceParams = Required<
  Pick<RouteParams, 'organizationsId' | 'fleetsId'>
>;

const CONFIG = DEFAULT_API_CONFIG;
const COLD_CHAIN_FLEET_CONFIG = {
  ...DEFAULT_API_CONFIG,
  axiosConfig: {
    ...DEFAULT_API_CONFIG.axiosConfig,
  },
};

export type FleetListParams = FleetRouteParams &
  PaginationContinuationInfo & { sessionId: string };

export const getFleets = makeThunk(
  'fleets/fetchFleets',
  makeGetPayloadCreator<ApiResponse<Fleet[]>, FleetRouteParams>({
    url: (_params: FleetRouteParams) => {
      const endpointUrl = `${globalThis.appConfig.apiBaseUrl}/organizations/${API_VERSION_DEFAULTS.default}/:organizationsId/fleets`;
      return endpointUrl;
    },
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: ({ organizationsId }) => {
      const requestParams = { organizationsId };
      return { requestParams };
    },
    responseAdapter: (response) => {
      const typed = response as ApiResponse<ApiFleetFields[]>;
      return {
        ...typed,
        result: Array.isArray(typed.result)
          ? typed.result.map((data) => mapApiResponseToFleet(data))
          : [],
      };
    },
  })
);

export const getPagedFleets = makeThunk(
  'fleets/getPagedFleets',
  makeGetPayloadCreator<
    ApiResponse<PagedResult<ApiFleetFields>>,
    FleetListParams
  >({
    url: () =>
      `${globalThis.appConfig.apiBaseUrl}/organizations/${API_VERSION_DEFAULTS.fleetListAPI}/:organizationsId/fleets`,
    axiosOptions: ({ continuationToken }, state) => {
      // add headers
      const baseHeaders = addAcceptLanguageHeader(
        CONFIG,
        state.profile.currentLocale
      );
      return continuationToken
        ? addHeader(baseHeaders, 'continuationToken', continuationToken)
        : baseHeaders;
    },
    argAdapter: ({ organizationsId, size }) => ({
      requestParams: {
        organizationsId,
        size: size.toString(),
      },
    }),
    responseAdapter: (response) => {
      const typed = response as ApiResponse<PagedResult<ApiFleetFields>>;
      return {
        ...typed,
        result: {
          items: typed?.result?.items || [],
          total_items: typed?.result?.total_items || 0,
          continuation_token: typed?.result?.continuation_token,
        },
      };
    },
  })
);

export const getFleetDetails = makeThunk(
  'fleets/fetchDetails',
  makeGetPayloadCreator<ApiResponse<Fleet>, Required<FleetRouteParams>>({
    url: `${globalThis.appConfig.apiBaseUrl}/organizations/${API_VERSION_DEFAULTS.coldChainFleetAPI}/:organizationsId/fleets/:fleetsId`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(
        COLD_CHAIN_FLEET_CONFIG,
        state.profile.currentLocale
      ),
    argAdapter: ({ organizationsId, fleetsId }) => ({
      requestParams: { organizationsId, fleetsId },
    }),
    responseAdapter: (response) => {
      const typed = response as ApiResponse<ApiFleetFields>;
      return {
        ...typed,
        result: mapApiResponseToFleet(typed?.result),
      };
    },
  })
);

export const getFleetSafetyCompliance = makeThunk(
  'fleets/fetchFleetSafetyCompliance',
  makeGetPayloadCreator<
    ApiResponse<SafetyComplianceSummary>,
    Required<FleetComplianceParams>
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}/views/safetyCompliance`,
    axiosOptions: ({ organizationsId }, state) =>
      addAcceptLanguageHeader(
        addOrganizationIdHeader(CONFIG, organizationsId),
        state.profile.currentLocale
      ),
    argAdapter: (params) => {
      return {
        requestParams: {
          fleetId: params.fleetsId,
        },
      };
    },
    responseAdapter: (response, _, params) => {
      if (
        response &&
        hasApiResult<ApiSafetyComplianceSummary>(
          response,
          (result): result is ApiSafetyComplianceSummary =>
            !!result && !!(result as ApiSafetyComplianceSummary).models
        )
      ) {
        return { ...response, result: response.result.models };
      }
      throw new BDError(
        'Unable to parse safety compliance response for fleet',
        {
          data: { response, params },
        }
      );
    },
  })
);

//TODO: remove if summary counts are returned with fleet list, or an endpoint is provided for this purpose (phase 2)
export const getFleetListAssetCounts = makeThunk(
  'fleets/aggregateListAssetCounts',
  makeGetPayloadCreator<
    ApiResponse<AggregateCounts<'assets'>>,
    FleetRouteParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/assets/${API_VERSION_DEFAULTS.default}`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (routeParams) => {
      return {
        requestParams: {
          organizationId: routeParams.organizationsId || '',
          fleetId: routeParams.fleetsId,
        },
      };
    },
    responseAdapter: (response: unknown, _, params) => {
      if (response && hasApiResult(response)) {
        return {
          ...response,
          result: Array.isArray(response?.result)
            ? response?.result.reduce(
                (fleetAssets: AggregateCounts<'assets'>, asset: Asset) => {
                  if (asset.fleet?.id) {
                    fleetAssets[asset.fleet.id] = {
                      assets: (fleetAssets[asset.fleet.id]?.assets || 0) + 1,
                    };
                  }
                  return fleetAssets;
                },
                {} as AggregateCounts<'assets'>
              )
            : {},
        };
      }
      throw new BDError('Unable to parse assets response for fleet', {
        data: { response, params },
      });
    },
  })
);

//TODO: remove if summary counts are returned with fleet list, or an endpoint is provided for this purpose (phase 2)
export const getFleetListUserCounts = makeThunk(
  'fleets/aggregateListUserCounts',
  makeGetPayloadCreator<
    ApiResponse<AggregateCounts<'users'>>,
    FleetRouteParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.default}`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (params) => ({
      requestParams: {
        organizationId: params.organizationsId || '',
      },
    }),
    responseAdapter: (response: unknown, _, params) => {
      if (response && hasApiResult(response)) {
        return {
          ...response,
          result: Array.isArray(response?.result)
            ? response?.result.reduce(
                (fleetUsers: AggregateCounts<'users'>, user: User) => {
                  if (user.fleet?.id) {
                    fleetUsers[user.fleet.id] = {
                      users: (fleetUsers[user.fleet.id]?.users || 0) + 1,
                    };
                  }
                  return fleetUsers;
                },
                {} as AggregateCounts<'users'>
              )
            : {},
        };
      }
      throw new BDError('Unable to parse users response for fleet', {
        data: { response, params },
      });
    },
  })
);

export const addFleet = makeThunk(
  'fleets/addNewFleet',
  makePostPayloadCreator<
    ApiResponse<Fleet>,
    FormPayload<FleetFormFields, 'organizationsId'>
  >({
    url: () => {
      return `${globalThis.appConfig.apiBaseUrl}/organizations/${API_VERSION_DEFAULTS.fleetAPI}/:organizationsId/fleets`;
    },
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: ({ formFields, params }) => {
      return {
        requestParams: {
          organizationsId: params.organizationsId,
        },
        requestBody: mapFleetFormToApiRequest(
          formFields as ValidFleetForm,
          true
        ),
      };
    },
  })
);

export const updateFleet = makeThunk(
  'fleets/updateFleet',
  makePutPayloadCreator<
    ApiResponse<Fleet>,
    {
      form: FormPayload<ValidFleetForm, 'organizationsId' | 'fleetsId'>;
      existing?: Fleet;
    }
  >({
    url: () => {
      return `${globalThis.appConfig.apiBaseUrl}/organizations/${API_VERSION_DEFAULTS.fleetListUpdateAPI}/:organizationsId/fleets/:fleetsId`;
    },
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(
        COLD_CHAIN_FLEET_CONFIG,
        state.profile.currentLocale
      ),
    argAdapter: ({ form }) => {
      const requestParams = {
        organizationsId: form.params.organizationsId,
        fleetsId: form.params.fleetsId,
      };
      const requestBody = mapFleetFormToApiRequest(form.formFields);

      return { requestParams, requestBody };
    },
    responseAdapter: (response) => {
      const typed = response as ApiResponse<ApiFleetFields>;
      return {
        ...typed,
        result: mapApiResponseToFleet(typed?.result),
      };
    },
  })
);

export const deleteFleet = makeThunk(
  'fleets/deleteFleet',
  makeDeletePayloadCreator<ApiResponse, Required<FleetRouteParams>>({
    url: `${globalThis.appConfig.apiBaseUrl}/organizations/${API_VERSION_DEFAULTS.default}/:organizationsId/fleets/:fleetsId`,
    argAdapter: ({ organizationsId, fleetsId }) => ({
      requestParams: { organizationsId, fleetsId },
    }),
  })
);

export const fleetsSlice = createSlice({
  name: 'fleets',
  initialState,
  reducers: {
    setFleetSessionConfig: (
      state,
      action: PayloadAction<FleetSessionConfigType>
    ) => {
      Object.keys(action.payload).forEach((key) => {
        const scope = key as FleetViewType;
        const payload = action.payload[scope] || {};
        Object.keys(payload).forEach((id) => {
          state.sessionConfigs = merge(
            state.sessionConfigs,
            {
              [scope]: {
                [id]: payload[id] && {
                  ...payload[id],
                },
              },
            },
            {
              arrayMerge: (_, sourceArray) => sourceArray,
            }
          );
        });
      });
    },
    resetFleetListSessionConfig: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      if (!state.sessionConfigs[FleetViewType.LIST]) {
        state.sessionConfigs[FleetViewType.LIST] = {};
      }
      state.sessionConfigs[FleetViewType.LIST][action.payload.sessionId] = {
        fleetCache: {},
        continuationToken: null,
        page: 0,
        totalItems: 0,
      };
    },
    setLastCreatedFleetId: (state, action: PayloadAction<string>) => {
      state.lastCreatedFleetId = action.payload;
    },
  },
  extraReducers: (builder) => {
    // fleet list
    builder.addCase(getFleets.pending, (state) => {
      state.operations[BDRequestType.GET_ALL] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
      fleetsAdapter.removeMany(state, state.ids); // ensure no records when fetching
    });
    builder.addCase(getFleets.fulfilled, (state, action) => {
      state.operations[BDRequestType.GET_ALL] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      fleetsAdapter.upsertMany(state, action.payload.result);
    });
    builder.addCase(getFleets.rejected, (state, action) => {
      state.operations[BDRequestType.GET_ALL] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.GET_ALL,
            severity: SeverityLevel.ERROR,
          },
        ],
      };
    });

    // fleet list paginated
    builder.addCase(getPagedFleets.pending, (state) => {
      state.operations[FleetRequestType.GET_PAGINATED_FLEET_LIST] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });

    builder.addCase(getPagedFleets.fulfilled, (state, action) => {
      state.operations[FleetRequestType.GET_PAGINATED_FLEET_LIST] = {
        status: BDRequestStatus.SUCCEEDED,
        errors: [],
      };

      const { items, total_items, continuation_token } = action.payload.result;
      const { page, sessionId } = action.meta.arg;
      if (sessionId) {
        fleetsAdapter.upsertMany(state, items.map(mapApiResponseToFleet));
        const updatedSessionId = '' + sessionId; // prepend empty string to satisfy CodeQL "remote property injection" rule check
        state.sessionConfigs = merge(
          state.sessionConfigs,
          {
            [FleetViewType.LIST]: {
              [updatedSessionId]: {
                fleetCache: {
                  [Number(page)]: {
                    data: items.map(mapApiResponseToFleet),
                    continuationToken: continuation_token || '',
                  },
                },
                continuationToken: continuation_token,
                totalItems: total_items,
              },
            },
          },
          {
            arrayMerge: (_, sourceArray) => sourceArray,
          }
        );
      }
    });
    builder.addCase(getPagedFleets.rejected, (state, action) => {
      state.operations[FleetRequestType.GET_PAGINATED_FLEET_LIST] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.GET_ALL,
          },
        ],
      };
    });

    // fleet details
    builder.addCase(getFleetDetails.pending, (state) => {
      state.operations[BDRequestType.GET_BY_ID] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
      state.operations[BDRequestType.GET_USER_SUMMARY] = {
        status: BDRequestStatus.IDLE,
      };
      state.operations[BDRequestType.GET_ASSET_SUMMARY] = {
        status: BDRequestStatus.IDLE,
      };
    });
    builder.addCase(getFleetDetails.fulfilled, (state, action) => {
      state.operations[BDRequestType.GET_BY_ID] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      // performs shallow merge update of existing records, _will overwrite nested object properties_
      fleetsAdapter.upsertOne(state, action.payload.result);
    });
    builder.addCase(getFleetDetails.rejected, (state, action) => {
      state.operations[BDRequestType.GET_BY_ID] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.GET_BY_ID,
          },
        ],
      };
    });

    // fleet safety compliance
    builder.addCase(getFleetSafetyCompliance.pending, (state) => {
      state.operations[FleetRequestType.GET_SAFETY_COMPLIANCE_SUMMARY] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(getFleetSafetyCompliance.fulfilled, (state, action) => {
      state.operations[FleetRequestType.GET_SAFETY_COMPLIANCE_SUMMARY] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      fleetSafetyComplianceAdapter.upsertOne(state.safetyCompliance, {
        fleetId: action.meta.arg.fleetsId,
        complianceSummary: action.payload.result,
      });
    });
    builder.addCase(getFleetSafetyCompliance.rejected, (state, action) => {
      state.operations[FleetRequestType.GET_SAFETY_COMPLIANCE_SUMMARY] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
          },
        ],
      };
    });

    // fleet list user counts
    //TODO: Remove once API adds usersCount to the fleet stats by ID (Phase 2)
    builder.addCase(getFleetListUserCounts.pending, (state) => {
      state.operations[BDRequestType.GET_USER_COUNTS] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(getFleetListUserCounts.fulfilled, (state, action) => {
      state.operations[BDRequestType.GET_USER_COUNTS] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      const userCounts = Object.entries(action.payload.result);
      if (userCounts.length > 0) {
        fleetsAdapter.updateMany(
          state,
          userCounts.map((userCount) => ({
            id: userCount[0],
            changes: {
              ...state.entities[userCount[0]],
              summaryCounts: {
                ...state.entities[userCount[0]]?.summaryCounts,
                users: userCount[1].users,
              },
            },
          }))
        );
      }
    });
    builder.addCase(getFleetListUserCounts.rejected, (state, action) => {
      state.operations[BDRequestType.GET_USER_COUNTS] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.GET_USER_COUNTS,
          },
        ],
      };
    });

    // fleet list asset counts
    //TODO: Remove once API adds assetCount to the fleet stats by ID (Phase 2)
    builder.addCase(getFleetListAssetCounts.pending, (state) => {
      state.operations[BDRequestType.GET_ASSET_COUNTS] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(getFleetListAssetCounts.fulfilled, (state, action) => {
      state.operations[BDRequestType.GET_ASSET_COUNTS] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      const assetCounts = Object.entries(action.payload.result);
      if (assetCounts.length > 0) {
        fleetsAdapter.updateMany(
          state,
          assetCounts.map((assetCount) => ({
            id: assetCount[0],
            changes: {
              ...state.entities[assetCount[0]],
              summaryCounts: {
                ...state.entities[assetCount[0]]?.summaryCounts,
                assets: assetCount[1].assets,
              },
            },
          }))
        );
      }
    });
    builder.addCase(getFleetListAssetCounts.rejected, (state, action) => {
      state.operations[BDRequestType.GET_ASSET_COUNTS] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.GET_ASSET_COUNTS,
          },
        ],
      };
    });

    // add fleet
    builder.addCase(addFleet.pending, (state) => {
      state.operations[BDRequestType.ADD] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(addFleet.fulfilled, (state) => {
      state.operations[BDRequestType.ADD] = {
        status: BDRequestStatus.SUCCEEDED,
      };
    });
    builder.addCase(addFleet.rejected, (state, action) => {
      state.operations[BDRequestType.ADD] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.ADD,
          },
        ],
      };
    });

    // update fleet
    builder.addCase(updateFleet.pending, (state) => {
      state.operations[BDRequestType.UPDATE] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(updateFleet.fulfilled, (state, action) => {
      state.operations[BDRequestType.UPDATE] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      fleetsAdapter.upsertOne(state, action.payload.result);
    });
    builder.addCase(updateFleet.rejected, (state, action) => {
      state.operations[BDRequestType.UPDATE] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.UPDATE,
          },
        ],
      };
    });

    // delete fleet
    builder.addCase(deleteFleet.pending, (state) => {
      state.operations[BDRequestType.DELETE] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(deleteFleet.fulfilled, (state) => {
      state.operations[BDRequestType.DELETE] = {
        status: BDRequestStatus.SUCCEEDED,
      };
    });
    builder.addCase(deleteFleet.rejected, (state, action) => {
      state.operations[BDRequestType.DELETE] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
            requestType: BDRequestType.DELETE,
          },
        ],
      };
    });
  },
});

export const selectFleetsState = (state: RootState): RootState['fleets'] =>
  state.fleets;

export const selectLastCreatedFleetId = (state: RootState) =>
  state.fleets.lastCreatedFleetId;

export const {
  selectAll: selectAllFleets,
  selectById: selectFleetById,
  selectIds: selectFleetIds,
} = fleetsAdapter.getSelectors<RootState>((state: RootState) => state.fleets);

export const {
  selectAll: selectAllSafetyCompliance,
  selectById: selectAllSafetyComplianceById,
  selectIds: selectAllSafetyComplianceByIds,
} = fleetSafetyComplianceAdapter.getSelectors<RootState>(
  (state: RootState) => state.fleets.safetyCompliance
);

export const {
  setFleetSessionConfig,
  resetFleetListSessionConfig,
  setLastCreatedFleetId,
} = fleetsSlice.actions;

export const fleetsReducer = fleetsSlice.reducer;
